import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { isAlwaysSupportedUrl } from '@shared/features/sidekick-navigator/types';
import {
  MeDocument,
  useUpdateUserSettingsMutation,
} from '@shared/generated/graphql';
import { useCallback, useMemo, useState } from 'react';
import {
  addAllowedDomain,
  getPageHostname,
  isDomainAllowed,
  isVoomaOwnedDomain,
} from './pageContentDomains';

export enum PageContentPermissionChoice {
  ALLOW = 'ALLOW',
  DONT_ALLOW = 'DONT_ALLOW',
}

export type PageContentPermission = {
  consumePageContentAccess: (pageUrl?: string) => string | null;
  pendingHostname: string | null;
  preparePageContentAccess: (pageUrl?: string) => void;
  respondToPendingRequest: (choice: PageContentPermissionChoice) => void;
};

export function usePageContentPermission(): PageContentPermission {
  const { me } = useMeContext();
  const { sendToast } = useToast();
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const [pendingHostname, setPendingHostname] = useState<string | null>(null);
  const [deniedHostname, setDeniedHostname] = useState<string | null>(null);
  const [domainsAllowedThisSession, setDomainsAllowedThisSession] = useState<
    string[]
  >([]);

  const savedAllowedDomains = useMemo(
    () => me?.settings?.browserExtensionDomainAllowlist ?? [],
    [me?.settings?.browserExtensionDomainAllowlist]
  );
  const allowedDomains = useMemo(
    () => [...savedAllowedDomains, ...domainsAllowedThisSession],
    [savedAllowedDomains, domainsAllowedThisSession]
  );

  const hasPersistentAccess = useCallback(
    (pageUrl: string, hostname: string) =>
      isAlwaysSupportedUrl(pageUrl) ||
      isVoomaOwnedDomain(hostname) ||
      isDomainAllowed(hostname, allowedDomains),
    [allowedDomains]
  );

  const preparePageContentAccess = useCallback(
    (pageUrl?: string) => {
      const hostname = pageUrl ? getPageHostname(pageUrl) : null;
      if (
        !pageUrl ||
        !hostname ||
        hasPersistentAccess(pageUrl, hostname) ||
        deniedHostname === hostname
      ) {
        setPendingHostname(null);
        return;
      }
      setDeniedHostname(null);
      setPendingHostname(hostname);
    },
    [deniedHostname, hasPersistentAccess]
  );

  const consumePageContentAccess = useCallback(
    (pageUrl?: string): string | null => {
      const hostname = pageUrl ? getPageHostname(pageUrl) : null;
      if (!pageUrl || !hostname) return null;
      if (hasPersistentAccess(pageUrl, hostname)) return hostname;

      setPendingHostname(hostname);
      setDeniedHostname(null);
      return null;
    },
    [hasPersistentAccess]
  );

  const saveAllowedDomain = useCallback(
    async (hostname: string) => {
      setDomainsAllowedThisSession((domains) =>
        addAllowedDomain(domains, hostname)
      );
      if (!me?.id) return;
      try {
        await updateUserSettings({
          variables: {
            input: {
              id: me.id,
              settings: {
                browserExtensionDomainAllowlist: addAllowedDomain(
                  savedAllowedDomains,
                  hostname
                ),
              },
            },
          },
          refetchQueries: [MeDocument],
        });
      } catch (error) {
        sendToast(`Couldn't save ${hostname} to your allowed sites`, {
          variant: 'error',
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [me?.id, savedAllowedDomains, sendToast, updateUserSettings]
  );

  const respondToPendingRequest = useCallback(
    (choice: PageContentPermissionChoice) => {
      if (!pendingHostname) return;

      setPendingHostname(null);
      if (choice === PageContentPermissionChoice.ALLOW) {
        setDeniedHostname(null);
        void saveAllowedDomain(pendingHostname);
        return;
      }
      setDeniedHostname(pendingHostname);
    },
    [pendingHostname, saveAllowedDomain]
  );

  return useMemo(
    () => ({
      consumePageContentAccess,
      pendingHostname,
      preparePageContentAccess,
      respondToPendingRequest,
    }),
    [
      consumePageContentAccess,
      pendingHostname,
      preparePageContentAccess,
      respondToPendingRequest,
    ]
  );
}
